import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { authService } from '../services/authService';
import { useAuthStore } from '../store/authStore';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { BackendStatus } from '@/components/shared/BackendStatus';
import { Eye, EyeOff } from 'lucide-react';

const loginSchema = z.object({
  email: z.string().email('البريد الإلكتروني غير صحيح'),
  password: z.string().min(6, 'كلمة المرور يجب أن تكون 6 أحرف على الأقل'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const LoginForm = () => {
  const location = useLocation();
  const { login } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      console.log('LoginForm: Starting login...');
      const response = await authService.login(data);
      
      const isAdminPage = location.pathname === '/admin/login';
      const userRole = String(response.user.role || '').trim();
      const hasAdminRole = userRole === 'Admin' || userRole === 'SuperAdmin' || userRole === '3' || userRole === '4';
      
      console.log('LoginForm: Login successful', {
        role: userRole,
        isAdminPage,
        hasAdminRole
      });
      
      // Save to zustand store
      login(response.token, response.user);
      
      // Small delay to ensure state is updated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      toast.success('تم تسجيل الدخول بنجاح!');
      
      // Small delay to ensure state is saved
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Navigate based on context
      if (isAdminPage) {
        if (hasAdminRole) {
          console.log('LoginForm: NOW redirecting to /admin');
          window.location.href = '/admin';
        } else {
          toast.error('ليس لديك صلاحية للوصول إلى لوحة التحكم');
          window.location.href = '/';
        }
      } else {
        // Player login
        if (hasAdminRole) {
          window.location.href = '/admin';
        } else {
          window.location.href = '/player';
        }
      }
    } catch (error: any) {
      const errorMessage = error.message || 'فشل تسجيل الدخول';
      toast.error(errorMessage, { duration: 5000 });
      console.error('LoginForm: Login error:', error);
    }
  };

  return (
    <div className="relative">
      <BackendStatus />
      <div className="absolute -top-10 -right-10 w-40 h-40 bg-accent-pink rounded-full opacity-20 blur-2xl" />
      <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-accent-greenLight rounded-full opacity-15 blur-2xl" />
      
      <Card className="w-full max-w-md mx-auto relative z-10 shadow-2xl border-2 border-accent-greenLight/30">
        <CardHeader className="bg-gradient-to-r from-accent-greenLight/20 to-primary pb-4">
          <CardTitle 
            className="text-3xl text-accent-dark text-center" 
            style={{ fontFamily: "'Almarai', 'Cairo', sans-serif" }}
          >
            تسجيل الدخول
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <Input
                type="email"
                placeholder="البريد الإلكتروني"
                {...register('email')}
                error={errors.email?.message}
                className="text-lg"
              />
            </div>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="كلمة المرور"
                {...register('password')}
                error={errors.password?.message}
                className="text-lg pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <Button 
              type="submit" 
              variant={location.pathname === '/admin/login' ? 'success' : 'secondary'}
              className="w-full text-lg py-6 shadow-lg hover:shadow-xl transition-all duration-300" 
              disabled={isSubmitting}
            >
              {isSubmitting ? <LoadingSpinner size="sm" /> : 'تسجيل الدخول'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
